import * as RadixPopover from "@radix-ui/react-popover";
import { CaretDown, Check, X } from "@phosphor-icons/react";
import { forwardRef, Fragment, type KeyboardEvent, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import {
  floatingCollisionPadding,
  floatingLayerClassName,
  searchableSelectContentCollisionClassName,
  searchableSelectListboxCollisionClassName
} from "@/components/ui/overlays/overlayPositioning";
import { Spinner } from "../../feedback/Spinner";
import { groupSelectOptions, type SelectMenuWidth, type SelectOption } from "../Select";

export type SearchableSelectProps = {
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string | undefined) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  clearable?: boolean;
  size?: "sm" | "md";
  /** Option popover width relative to the trigger. Defaults to `"auto"`. */
  menuWidth?: SelectMenuWidth;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

export const SearchableSelect = forwardRef<HTMLButtonElement, SearchableSelectProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = "Select",
      searchPlaceholder = "Search...",
      emptyText = "No results found",
      loading,
      disabled,
      error,
      clearable = true,
      size = "md",
      menuWidth = "auto",
      className,
      id,
      "aria-describedby": describedBy,
      "aria-invalid": ariaInvalid
    },
    ref
  ) => {
    const menuWidthClass =
      menuWidth === "trigger"
        ? "bfrs-w-[var(--radix-popover-trigger-width)]"
        : typeof menuWidth === "number"
          ? ""
          : "bfrs-min-w-[var(--radix-popover-trigger-width)]";
    const menuWidthStyle = typeof menuWidth === "number" ? { width: menuWidth, minWidth: menuWidth } : undefined;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const selected = options.find((option) => option.value === value);
    const filteredOptions = useMemo(
      () => options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase())),
      [options, query]
    );

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        setQuery("");
        setActiveIndex(0);
      }
    };

    const commitValue = (nextValue: string | undefined) => {
      onValueChange?.(nextValue);
      handleOpenChange(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
      if (!open && ["ArrowDown", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        handleOpenChange(true);
        return;
      }
      if (!open) return;
      if (event.key === "Escape") handleOpenChange(false);
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
      }
      if (event.key === "Enter" && filteredOptions[activeIndex]) {
        event.preventDefault();
        commitValue(filteredOptions[activeIndex].value);
      }
    };

    return (
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
        <RadixPopover.Trigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? error}
            data-filled={selected && !error ? "" : undefined}
            className={cn(
              "bfrs-flex bfrs-w-full bfrs-items-center bfrs-justify-between bfrs-gap-2 bfrs-rounded-lg bfrs-border bfrs-bg-surface-card bfrs-text-left bfrs-outline-none bfrs-transition focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/20 data-[state=open]:bfrs-ring-2 data-[state=open]:bfrs-ring-border-focus/20 aria-[expanded=true]:bfrs-ring-2 aria-[expanded=true]:bfrs-ring-border-focus/20 disabled:bfrs-cursor-not-allowed disabled:bfrs-bg-surface-app disabled:bfrs-text-text-disabled",
              cn("bfrs-rounded-[7px] bfrs-px-3 bfrs-text-[12px]", size === "sm" ? "bfrs-h-7" : "bfrs-h-9"),
              error
                ? "bfrs-border-danger focus-visible:bfrs-border-danger focus-visible:bfrs-ring-danger/15 data-[state=open]:bfrs-border-danger data-[state=open]:bfrs-ring-danger/15 aria-[expanded=true]:bfrs-border-danger aria-[expanded=true]:bfrs-ring-danger/15"
                : cn("bfrs-border-border focus-visible:bfrs-border-border-focus data-[state=open]:bfrs-border-border-focus aria-[expanded=true]:bfrs-border-border-focus", size === "sm" && "data-[filled]:bfrs-border-border-focus"),
              selected ? "bfrs-text-text-primary" : "bfrs-text-text-placeholder",
              className
            )}
            onKeyDown={handleKeyDown}
          >
            <span className="bfrs-truncate">{selected?.label ?? placeholder}</span>
            <span className="bfrs-flex bfrs-items-center bfrs-gap-1 bfrs-text-text-muted">
              {clearable && selected && (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label="Clear selection"
                  className="bfrs-rounded-sm bfrs-p-0.5 hover:bfrs-bg-surface-muted"
                  onClick={(event) => {
                    event.stopPropagation();
                    commitValue(undefined);
                  }}
                >
                  <X size={14} />
                </span>
              )}
              <CaretDown className={cn("bfrs-transition-transform bfrs-duration-150", open && "bfrs-rotate-180")} size={10} />
            </span>
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <RadixPopover.Content
              align="start"
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                inputRef.current?.focus();
              }}
              onKeyDown={handleKeyDown}
              style={menuWidthStyle}
              className={cn(
                floatingLayerClassName,
                "bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-1 bfrs-shadow-elevated bfrs-outline-none bfrs-animate-content-in",
                menuWidthClass,
                searchableSelectContentCollisionClassName
              )}
            >
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
                placeholder={searchPlaceholder}
                className="bfrs-mb-1 bfrs-h-7 bfrs-w-full bfrs-rounded-[7px] bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-px-3 bfrs-text-[12px] bfrs-text-text-primary placeholder:bfrs-text-text-placeholder bfrs-outline-none focus:bfrs-border-border-focus focus:bfrs-ring-2 focus:bfrs-ring-border-focus/20 focus-visible:bfrs-border-border-focus focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/20"
              />
              <div role="listbox" className={searchableSelectListboxCollisionClassName}>
                {loading ? (
                  <div className="bfrs-flex bfrs-h-16 bfrs-items-center bfrs-justify-center bfrs-text-text-muted">
                    <Spinner size="sm" />
                  </div>
                ) : filteredOptions.length === 0 ? (
                  <div className="bfrs-px-3 bfrs-py-4 bfrs-text-sm bfrs-text-text-muted">{emptyText}</div>
                ) : (
                  (() => {
                    let flatIndex = -1;
                    return groupSelectOptions(filteredOptions).map((grp, groupIndex) => (
                      <Fragment key={`group-${groupIndex}`}>
                        {grp.group && (
                          <div className="bfrs-px-3 bfrs-pb-1 bfrs-pt-2 bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.08em] bfrs-text-text-muted">
                            {grp.group}
                          </div>
                        )}
                        {grp.items.map((option) => {
                          flatIndex += 1;
                          const index = flatIndex;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              role="option"
                              aria-selected={option.value === value}
                              disabled={option.disabled}
                              className={cn(
                                "bfrs-flex bfrs-w-full bfrs-cursor-pointer bfrs-items-center bfrs-justify-between bfrs-rounded-sm bfrs-px-3 bfrs-py-2 bfrs-text-left bfrs-text-[12px] bfrs-text-text-primary bfrs-outline-none hover:bfrs-bg-primary-soft disabled:bfrs-cursor-not-allowed disabled:bfrs-text-text-disabled",
                                option.value === value && "bfrs-bg-primary-soft bfrs-font-bold",
                                activeIndex === index && "bfrs-bg-primary-soft"
                              )}
                              onMouseEnter={() => setActiveIndex(index)}
                              onClick={() => commitValue(option.value)}
                            >
                              {option.label}
                              {option.value === value && <Check size={16} weight="bold" className="bfrs-text-primary" />}
                            </button>
                          );
                        })}
                      </Fragment>
                    ));
                  })()
                )}
              </div>
            </RadixPopover.Content>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }
);

SearchableSelect.displayName = "SearchableSelect";
